import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EnvironmentInjector,
  inject,
  input,
  Signal,
  signal
} from '@angular/core';
import { ensureAgGridSetup } from '@softapps/core';
import { AgCharts } from 'ag-charts-angular';
import type { AgChartOptions } from 'ag-charts-community';
import { Chart, ChartValue } from '../types/chart';
import { ChartDataCache, toAgChartOptions } from './to-ag-chart-options.function';
import { formatter } from '@softline/ui-core';
import { AppearanceService } from '@softline/application';

@Component({
  selector: 'soft-chart',
  imports: [AgCharts],
  template: `
    <div class="chart-host">
      @defer (when agReady()) {
        <ag-charts class="block"
                   [options]="chartOptions()" />
      }
    </div>
  `,
  styles: `
    :host { display: block; width: 100%; height: 100%; }
    .chart-host { position: relative; width: 100%; height: 100%; }
    /* Without this ag-charts keeps its own 300px default and paints outside a
       shorter host — its wrapper also carries a 300px min-width. */
    .chart-host > ag-charts { display: block; width: 100%; height: 100%; }
    .annotation-cell > * { pointer-events: auto; }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent<T extends object, TValue extends ChartValue> {
  private readonly cache = new ChartDataCache();
  readonly config = input.required<Chart<T>>();
  readonly f = formatter();
  readonly injector = inject(EnvironmentInjector);
  readonly appearanceService = inject(AppearanceService);

  // gates the @defer around <ag-charts> until the lazily loaded enterprise
  // registration (ensureAgGridSetup) has run
  protected readonly agReady = signal(false);

  readonly chartOptions: Signal<AgChartOptions> = computed(() => {
    const config = this.config();
    const agChartOptions = toAgChartOptions(
      config, {
        formatter: this.f,
        injector: this.injector,
        cache: this.cache,
        appearanceService: this.appearanceService,
      });
    return agChartOptions;
  });

  constructor() {
    void ensureAgGridSetup().then(() => this.agReady.set(true));
  }
}
